/**
 * Maps free text typed into "Other practice area" onto one of our real
 * practice-area categories.
 *
 * Consumers describe their problem, not its legal label — "wife filed for
 * divorce" rather than "Family Law". When what they typed clearly belongs
 * under a category we already have, we filter by that category instead of
 * treating the entry as a genuine "Other" and searching listing text for it.
 *
 * Matching is intentionally tolerant of inflection ("divorce" also catches
 * "divorced", "divorcing") but still anchored on word boundaries, so "sue"
 * does not fire on "issue". Anything that does not reasonably match stays a
 * genuine Other entry and falls through to the free-text search.
 */

const PA_KEYWORDS = {
  "Family Law": [
    "divorce", "custody", "child custody", "visitation", "child support",
    "alimony", "spousal support", "prenup", "prenuptial agreement",
    "postnuptial agreement", "paternity", "adoption", "domestic violence",
    "restraining order", "protective order", "separation", "legal separation",
    "move-away", "relocation", "parental rights", "guardianship",
  ],
  "Civil Litigation": [
    "lawsuit", "sue", "suing", "being sued", "breach of contract", "fraud",
    "misrepresentation", "property dispute", "defamation", "libel", "slander",
    "injunction", "collections", "judgment", "damages",
  ],
  "Business / Corporate / Contracts": [
    "llc", "incorporate", "incorporation", "business formation",
    "operating agreement", "shareholder agreement", "contract review",
    "partnership dispute", "sell my business", "buy a business",
    "corporate governance", "vendor agreement", "nda", "non-disclosure",
  ],
  "Labor & Employment": [
    "fired", "wrongful termination", "discrimination", "harassment",
    "sexual harassment", "retaliation", "unpaid wages", "overtime",
    "wage theft", "employment contract", "independent contractor", "1099",
    "fmla", "workplace accommodation", "severance",
  ],
  "Criminal Defense / Post-Conviction": [
    "dui", "dwi", "arrested", "criminal charge", "misdemeanor", "felony",
    "drug charge", "assault", "battery", "theft", "burglary", "robbery",
    "domestic violence charge", "weapons charge", "probation", "parole",
    "expungement", "record clearing", "appeal",
  ],
  "Personal Injury / Insurance": [
    "car accident", "truck accident", "motorcycle accident",
    "pedestrian accident", "bike accident", "slip and fall", "dog bite",
    "wrongful death", "product liability", "injured", "injury claim",
    "insurance claim", "insurance denied", "bad faith insurance",
    "uninsured motorist",
  ],
  "Estate Planning / Probate": [
    "will", "living trust", "power of attorney", "healthcare directive",
    "probate", "trust administration", "will contest", "trust contest",
    "inheritance", "beneficiary dispute", "conservatorship", "elder law",
  ],
  Immigration: [
    "green card", "citizenship", "naturalization", "visa", "work visa",
    "student visa", "fiance visa", "fiancé visa", "asylum", "refugee",
    "deportation", "removal", "daca", "consular processing",
  ],
  "Real Estate / Construction": [
    "buying a house", "selling a house", "landlord", "tenant", "eviction",
    "lease dispute", "property line", "boundary dispute", "easement",
    "title dispute", "hoa", "construction defect", "contractor dispute",
    "mechanic's lien", "zoning",
  ],
  "Bankruptcy / Debt Relief": [
    "bankruptcy", "chapter 7", "chapter 13", "debt collection",
    "debt collector", "creditor harassment", "foreclosure", "mortgage debt",
    "repossession", "repo", "wage garnishment", "garnishment", "judgment debt",
    "debt settlement", "debt negotiation", "credit report dispute",
    "credit reporting error", "can't pay my debts", "behind on payments",
  ],
};

/** Lowercase, drop punctuation, collapse whitespace. Applied to both sides. */
function normalize(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Word-boundary match that tolerates the common inflections of the phrasing
 * people actually type: "divorce" catches "divorced" and "divorcing",
 * "evict" catches "eviction". Built once per keyword and cached.
 */
const patternCache = new Map();

function keywordPattern(keyword) {
  let pattern = patternCache.get(keyword);
  if (!pattern) {
    const body = escapeRegExp(keyword).replace(/\s+/g, "\\s+");
    // Plural/past forms are safe on anything but the very shortest keywords.
    // "-ing" needs a longer stem or it turns "will" into "willing"; the
    // optional "e" lets "divorce" reach "divorcing" as well as "divorced".
    const suffixes = [];
    if (keyword.length >= 3) suffixes.push("e?[sd]", "es");
    if (keyword.length >= 6) suffixes.push("e?ing");
    const tail = suffixes.length ? `(?:${suffixes.join("|")})?` : "";
    pattern = new RegExp(`\\b${body}${tail}\\b`);
    patternCache.set(keyword, pattern);
  }
  return pattern;
}

/**
 * Resolve free text to a practice-area category name, or "" when nothing
 * reasonably matches. The longest matching keyword wins, so "domestic
 * violence charge" resolves to criminal defence rather than family law, and
 * a category typed by its own name always wins outright.
 */
export function matchPracticeArea(text) {
  const haystack = normalize(text);
  if (!haystack) return "";

  let best = "";
  let bestScore = 0;

  for (const [area, keywords] of Object.entries(PA_KEYWORDS)) {
    // Someone who types the category name itself means exactly that.
    if (haystack.includes(normalize(area))) return area;

    for (const keyword of keywords) {
      const normalized = normalize(keyword);
      if (!normalized) continue;
      if (!keywordPattern(normalized).test(haystack)) continue;
      // Longer phrases are more specific, so they outrank a bare word that
      // several categories share.
      if (normalized.length > bestScore) {
        bestScore = normalized.length;
        best = area;
      }
    }
  }

  return best;
}

export default matchPracticeArea;
